/**
 * ICP Wallet Connector using Internet Identity
 * Supports ALL ICP wallets (Plug, Stoic, NFID, NNS, etc.)
 */

import { AuthClient } from '@dfinity/auth-client';
import { toast } from 'sonner';

// Auth client singleton
let authClient: AuthClient | null = null;

/**
 * Initialize Internet Identity auth client
 */
async function getAuthClient(): Promise<AuthClient> {
  if (authClient) return authClient;
  
  try {
    authClient = await AuthClient.create({
      idleOptions: {
        disableIdle: true, // Keep session active
      },
    });
    return authClient;
  } catch (error) {
    console.error('Failed to create ICP auth client:', error);
    throw new Error('Failed to initialize ICP authentication');
  }
}

/**
 * Connect ICP wallet via Internet Identity
 * This works with ALL ICP wallets (Plug, Stoic, NFID, etc.)
 */
export async function connectICPWallet(): Promise<{ principal: string }> {
  try {
    const client = await getAuthClient();
    
    // Check if already authenticated
    const isAuthenticated = await client.isAuthenticated();
    
    if (isAuthenticated) {
      const identity = client.getIdentity();
      const principal = identity.getPrincipal().toString();
      
      if (principal && principal !== '2vxsx-fae') { // Not anonymous
        toast.success('ICP Wallet Connected', {
          description: `Principal: ${principal.substring(0, 20)}...`,
        });
        return { principal };
      }
    }
    
    // Start Internet Identity login
    return new Promise((resolve, reject) => {
      client.login({
        identityProvider: 'https://identity.ic0.app',
        onSuccess: () => {
          const identity = client.getIdentity();
          const principal = identity.getPrincipal().toString();
          
          if (!principal || principal === '2vxsx-fae') {
            reject(new Error('Failed to authenticate with Internet Identity'));
            return;
          }
          
          toast.success('ICP Wallet Connected', {
            description: `Principal: ${principal.substring(0, 20)}...`,
            duration: 5000,
          });
          
          resolve({ principal });
        },
        onError: (error) => {
          console.error('Internet Identity login failed:', error);
          toast.error('ICP Connection Failed', {
            description: error || 'Failed to connect via Internet Identity',
            duration: 5000,
          });
          reject(new Error(error || 'Internet Identity login failed'));
        },
      });
    });
  } catch (error: any) {
    console.error('ICP wallet connection error:', error);
    toast.error('ICP Connection Error', {
      description: error.message || 'Failed to connect ICP wallet',
      duration: 5000,
    });
    throw error;
  }
}

/**
 * Disconnect ICP wallet
 */
export async function disconnectICPWallet(): Promise<void> {
  try {
    const client = await getAuthClient();
    await client.logout();
    authClient = null;
    
    toast.info('ICP Wallet Disconnected', {
      description: 'Successfully disconnected from Internet Identity',
    });
  } catch (error) {
    console.error('ICP disconnect error:', error);
    // Don't show error toast for disconnect failures
  }
}

/**
 * Get current ICP principal (if connected)
 */
export async function getICPPrincipal(): Promise<string | null> {
  try {
    const client = await getAuthClient();
    const isAuthenticated = await client.isAuthenticated();
    
    if (!isAuthenticated) return null;
    
    const identity = client.getIdentity();
    const principal = identity.getPrincipal().toString();
    
    if (principal === '2vxsx-fae') return null; // Anonymous
    
    return principal;
  } catch (error) {
    console.error('Failed to get ICP principal:', error);
    return null;
  }
}

/**
 * Check if ICP wallet is connected
 */
export async function isICPWalletConnected(): Promise<boolean> {
  const principal = await getICPPrincipal();
  return principal !== null;
}

